﻿import {arrayEquals, NamedValue} from "@2d-front-monorepo/core";
import React, {JSX, memo, useCallback, useMemo} from "react";
import {useControlId} from "../helpers/useControlId";
import OptionGroup, {OptionGroupEntry} from "../HtmlControls/OptionGroup";
import {IMvvmContextState, MvvmContextState} from "./MvvmContextState";
import {MvvmControlProps} from "./MvvmControlProps";

import {useRegisterValidation} from "./useRegisterValidation";
import {useSelectedItems} from "./useSelectedItems";

export type MvvmMultiOptionGroupEntry<TValue> = NamedValue<TValue> & {
	disabled?: boolean;
}

export type MvvmMultiOptionGroupProps<T extends object = never, TValue = never> = MvvmControlProps<T, TValue[]> & {
	items: MvvmMultiOptionGroupEntry<TValue>[];
}

type InternalMultiOptionGroupProps<T extends object, TValue> = MvvmMultiOptionGroupProps<T, TValue> & {
	context: IMvvmContextState<T>;
	value?: TValue[];
}

function InternalOptionGroup<T extends object, TValue>(props: InternalMultiOptionGroupProps<T, TValue>): JSX.Element {
	const {
		property,
		id,
		label,
		items,
		column,
		hidden,
		disabled,
		validation,
		context,
		value
	} = props;

	const controlId = useControlId(id);
	const itemMap = useMemo<OptionGroupEntry[]>(() => items.map((x, index) => ({
		value: index.toString(),
		label: x.label,
		disabled: x.disabled
	})), [items]);

	const selectedValues = useSelectedItems(items, value);

	const onChange = useCallback(function (s: number[]): void {
		const selected = s
			.map(x => items.at(x)?.value)
			.filter(x => x != null);

		(context as MvvmContextState<T>).setValue(property, selected);
	}, [context, property, validation, controlId]);

	useRegisterValidation(context, property, controlId, validation);

	if (hidden) {
		return <></>;
	}

	return <OptionGroup label={label}
	                    name={controlId}
	                    items={itemMap}
	                    values={selectedValues}
	                    disabled={disabled}
	                    column={column}
	                    hidden={hidden}
	                    multiple={true}
	                    onChange={onChange}/>;
}

const MemoizedInternalOptionGroup = memo(
	InternalOptionGroup,
	(prevProps, nextProps) => {
		return (
			arrayEquals(prevProps.value, nextProps.value) &&
			arrayEquals(prevProps.items, nextProps.items) &&
			prevProps.hidden === nextProps.hidden &&
			prevProps.disabled === nextProps.disabled &&
			prevProps.validation === nextProps.validation &&
			prevProps.property === nextProps.property &&
			prevProps.column === nextProps.column
		);
	}
) as unknown as <T extends object, TValue>(props: InternalMultiOptionGroupProps<T, TValue>) => JSX.Element;

export default function MvvmOptionGroup<T extends object = never, TValue = never>(props: MvvmMultiOptionGroupProps<T, TValue>): JSX.Element {
	const {context, ...rest} = props;
	return <MemoizedInternalOptionGroup {...rest} context={context}
	                                    value={context.getValue<TValue[]>(props.property)}/>;
}
